import {
  IMultipleRowActions,
  Modal,
  OverflowTooltip,
  Table,
} from "@4uhub/lib4uhub";
import ReplayIcon from "@mui/icons-material/Replay";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Chip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { GridApiCommunity } from "@mui/x-data-grid/internals";
import { format } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";
import { MutableRefObject, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import MainApi from "../../../../services/mainApi.service";
import MacroFilter from "./MacroFilter";
import SyncTransactionModal from "./SyncTransactionActionModal";
import useRestartSyncTransactions, {
  isSyncTransactionError,
} from "./useRestartSyncTransactions";
import { ISyncTransaction } from "../../../../models/sync-transaction";
import {
  FilterParamEntry,
  FilterParamFromEntries,
  IFilterParams,
} from "./models";

const TODAY = new Date();

const FILTER_PARAMS_ITEMS = {
  createdStart: { value: TODAY, label: "start_date" },
  createdEnd: { value: TODAY, label: "end_date" },
  name: { value: "", label: "name" },
  document: { value: "", label: "cpf" },
  externalId: { value: "", label: "external_id" },
  statusCode: { value: "", label: "status_item" },
};

const STATUS_ITEMS: Record<
  string,
  {
    color:
      | "warning"
      | "info"
      | "success"
      | "error"
      | "primary"
      | "secondary"
      | "default";
    key: string;
  }
> = {
  ERRO: { color: "error", key: "error" },
  SUCESSO: { color: "success", key: "success" },
  "EM PROCESSAMENTO": { color: "info", key: "in_process" },
  PENDENTE: { color: "warning", key: "pending" },
  "AGUARDANDO GERAÇÃO DAS AÇÕES": {
    color: "default",
    key: "waiting_action_generation",
  },
};

const SYNC_TRANSACTION_ROUTE =
  process.env.REACT_APP_EXTERNAL + "/api/v1/SyncTransaction";

const syncTransactionService = new MainApi<ISyncTransaction>(
  SYNC_TRANSACTION_ROUTE,
);

const translationPath = "page.4uBus.logs.syncTransaction.";

const SyncTransaction = () => {
  const [refresh, setRefresh] = useState(0);

  const [modalOpen, setModalOpen] = useState(false);

  const [selectedRow, setSelectedRow] = useState<ISyncTransaction | null>(null);

  const [filterParams, setFilterParams] =
    useState<IFilterParams>(FILTER_PARAMS_ITEMS);

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const formatDate = useCallback(
    (value?: string | null) => {
      if (!value) return "-";

      return format(new Date(value), "Pp", {
        locale: language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
      }).replaceAll(",", "");
    },
    [language],
  );

  const {
    canRestart,
    restart,
    loading: restarting,
  } = useRestartSyncTransactions();

  const columns: GridColDef<ISyncTransaction>[] = [
    {
      field: "identifier",
      headerName: t(translationPath + "identifier") || "Identifier",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "name",
      headerName: t(translationPath + "name") || "Name",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "document",
      headerName: t(translationPath + "cpf") || "CPF",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "statusCode",
      headerName: t(translationPath + "status_item") || "Status",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        if (!params.value) return "-";

        const status = params.value.toUpperCase();

        const getStatusConfig = (status: string) => {
          return (
            STATUS_ITEMS[status] || {
              color: "warning" as const,
              key: "pending",
            }
          );
        };

        const config = getStatusConfig(status);

        return (
          <Chip
            label={t(translationPath + `status.${config.key}`)}
            color={config.color}
            size="small"
          />
        );
      },
    },
    {
      field: "externalId",
      headerName: t(translationPath + "external_id") || "External ID",
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "originSoftwareName",
      headerName: t(translationPath + "origin_system") || "Origin system",
      align: "left",
      headerAlign: "left",
      renderCell: (params) => (
        <OverflowTooltip
          text={params.value || "-"}
          tooltip={params.value || "-"}
          textTransform="none"
          variant="body2"
        />
      ),
    },
    {
      field: "created",
      headerName: t(translationPath + "created") || "Created",
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        const value = formatDate(params.value);

        return (
          <OverflowTooltip
            text={value}
            tooltip={value}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "individualLastModified",
      headerName:
        t(translationPath + "individual_last_modified") || "Source update",
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        const value = formatDate(params.value);

        return (
          <OverflowTooltip
            text={value}
            tooltip={value}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
  ];

  const params = useMemo(() => {
    const filterParamsArray = Object.entries(
      filterParams,
    ) as FilterParamEntry[];
    const filterParamsFormatted = Object.fromEntries(
      filterParamsArray.map(([key, value]) => [key, value?.value]),
    ) as FilterParamFromEntries;

    return {
      refresh,
      ...filterParamsFormatted,
      createdStart: filterParamsFormatted.createdStart
        ? new Date(filterParamsFormatted.createdStart.setHours(15))
            .toISOString()
            .substring(0, 10)
        : undefined,
      createdEnd: filterParamsFormatted.createdEnd
        ? new Date(filterParamsFormatted.createdEnd.setHours(15))
            .toISOString()
            .substring(0, 10)
        : undefined,
    };
  }, [refresh, filterParams]);

  const handleFilterChange = useCallback(
    (newFilters: IFilterParams | ((prev: IFilterParams) => IFilterParams)) => {
      setFilterParams(newFilters);
      setRefresh((prev) => prev + 1);
    },
    [],
  );

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedRow(null);
  }, []);

  const handleRestart = useCallback(
    async (
      identifiers: number[],
      api?: MutableRefObject<GridApiCommunity>,
    ) => {
      const { executed } = await restart(identifiers);

      if (!executed) return;

      api?.current?.setRowSelectionModel([]);
      setRefresh((prev) => prev + 1);
    },
    [restart],
  );

  const multipleRowsActions = useMemo<IMultipleRowActions[]>(
    () =>
      canRestart
        ? [
            {
              action: async (_, api) => {
                const selectedRows = api.current?.getSelectedRows?.();
                const identifiers = selectedRows
                  ? Array.from(selectedRows.values()).map((row) =>
                      Number(row.identifier),
                    )
                  : [];

                await handleRestart(identifiers, api);
              },
              icon: <ReplayIcon fontSize="small" />,
              text: t(translationPath + "resend") || "Forçar reenvio",
            },
          ]
        : [],
    [canRestart, handleRestart, t],
  );

  return (
    <>
      <Modal open={modalOpen} onClose={handleCloseModal} maxDialogWidth="lg">
        {selectedRow && (
          <SyncTransactionModal
            syncTransaction={selectedRow}
            onClose={handleCloseModal}
            onRestarted={() => {
              handleCloseModal();
              setRefresh((prev) => prev + 1);
            }}
          />
        )}
      </Modal>
      <Table<ISyncTransaction>
        title={t(translationPath + "title")}
        columns={columns}
        service={syncTransactionService}
        searchInputPlaceHolder={
          t(translationPath + "search_placeholder") || "Search..."
        }
        showDefaultMacroFilters={false}
        showButton={false}
        showDeleteButton={false}
        defaultActions={true}
        multipleRowsActions={multipleRowsActions}
        syncing={restarting}
        getRowId={(row) => (row.identifier ? row.identifier : 0)}
        rowActions={(params) => [
          {
            action: () => {
              setSelectedRow(params.row);
              setModalOpen(true);
            },
            icon: <VisibilityIcon fontSize="small" />,
            text: t(translationPath + "view_action") || "View action",
          },
          canRestart && isSyncTransactionError(params.row.statusCode)
            ? {
                action: () => {
                  handleRestart([Number(params.row.identifier)]);
                },
                icon: <ReplayIcon fontSize="small" />,
                text: t(translationPath + "resend_row") || "Reenviar",
              }
            : null,
        ]}
        serviceProps={params}
        macroFilters={useCallback(
          () => (
            <MacroFilter
              statusItems={STATUS_ITEMS}
              filterParams={filterParams}
              onFilterParams={handleFilterChange}
            />
          ),
          [filterParams, handleFilterChange],
        )}
      />
    </>
  );
};

export default SyncTransaction;
